import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  AssetCard,
  Button,
  ConnectedAnnotatedField,
  PrimaryFullScreenSpinner,
  Tabs,
  TabsList,
  TabsTrigger,
  Txt,
  toast,
  useAppForm,
} from "@repo/ui";
import { useStore } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Decimal from "decimal.js";
import { Suspense, useEffect } from "react";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/payouts_/new")({
  head: () => opsHead("New Payout"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const scheduleValues = ["NOW", "LATER"] as const;

const formSchema = z
  .object({
    organisationId: z.string().nonempty({ message: "Required" }),
    businessEntityId: z.string().nonempty({ message: "Required" }),
    beneficiaryAccountId: z.string().nonempty({ message: "Required" }),
    amount: z
      .custom<Decimal>(Decimal.isDecimal, {
        message: "Please enter an amount",
      })
      .refine((val) => val?.gt(1), "Amount must be greater than $1")
      .describe("Amount"),
    schedule: z.enum(scheduleValues),
    date: z.date().optional(),
    reference: z.string().optional(),
    suppressNotifications: z.boolean(),
  })
  .refine(
    (data) => {
      if (data.schedule === "LATER") {
        return data.date !== undefined;
      }
      return true;
    },
    {
      message: "Date is required",
      path: ["date"],
    },
  );

type FormData = z.infer<typeof formSchema>;

function View() {
  const navigate = useNavigate();

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminNewPayoutForm {
        organisations {
          ...AdminNewPayoutFormOrg
        }
      }

      fragment AdminNewPayoutFormOrg on Organisation {
        id
        name
        businessEntities {
          id
          name
          beneficiaryAccounts {
            id
            ... BeneficiaryAssetCardFields
          }
        }
      }
  `),
  );

  const [createPayoutMutation, { loading }] = useMutation(
    graphql(`
      mutation AdminNewPayoutForm_Create($input: CreateNewPayoutInput!) {
        createNewPayout(input: $input) {
          payout {
            id
            paymentReference
          }
        }
      }
    `),
  );

  if (!data.organisations) {
    throw new Error("expected organisations");
  }

  const form = useAppForm({
    defaultValues: {
      schedule: "NOW",
      suppressNotifications: false,
    } as FormData,
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value }) => {
      const defaultErrorMessage =
        "There was an error submitting your form. Please try again later.";

      let date: Date | undefined;
      if (value.schedule === "LATER") {
        if (!value.date) {
          throw new Error("date required when schedule is LATER");
        }
        date = value.date;
      }

      await createPayoutMutation({
        variables: {
          input: {
            amount: value.amount,
            date,
            reference: value.reference ?? "",
            beneficiaryAccountId: value.beneficiaryAccountId,
            organisationId: value.organisationId,
            suppressNotifications: value.suppressNotifications,
          },
        },
        onCompleted: (data) => {
          if (!data.createNewPayout?.payout.id) {
            toast({
              kind: "error",
              title: "Failed to create payout",
              description: defaultErrorMessage,
            });
            throw new Error("failed to create payout");
          } else {
            toast({
              kind: "success",
              title: "Created payout",
              description: `Created new payout ${data.createNewPayout.payout.paymentReference}`,
            });
            navigate({
              to: "/payouts/$payoutId",
              params: { payoutId: data.createNewPayout.payout.id },
            });
          }
        },
        onError: (error) => {
          const errorMessage =
            error.graphQLErrors?.[0]?.message || defaultErrorMessage;
          toast({
            kind: "error",
            title: "Failed to create payout",
            description: errorMessage,
          });
          throw error;
        },
      });
    },
  });

  const organisationOptions = data.organisations
    .map((org) => ({
      value: org.id,
      label: org.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const selectedOrganisationId = useStore(
    form.store,
    (state) => state.values.organisationId,
  );
  const selectedOrganisation = data.organisations.find(
    (o) => o.id === selectedOrganisationId,
  );

  const selectedBusinessEntityId = useStore(
    form.store,
    (state) => state.values.businessEntityId,
  );
  const selectedBusinessEntity = selectedOrganisation?.businessEntities?.find(
    (be) => be.id === selectedBusinessEntityId,
  );

  const businessEntityOptions = selectedOrganisation?.businessEntities
    ?.map((be) => ({
      value: be.id,
      label: be.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const selectedBeneficiaryAccountId = useStore(
    form.store,
    (state) => state.values.beneficiaryAccountId,
  );
  const selectedSchedule = useStore(
    form.store,
    (state) => state.values.schedule,
  );

  const beneficiaryAccountOptions = selectedBusinessEntity?.beneficiaryAccounts
    ?.map((a) => ({
      value: a.id,
      beneficiaryAccount: a,
    }))
    .sort((a, b) =>
      a.beneficiaryAccount.accountName.localeCompare(
        b.beneficiaryAccount.accountName,
      ),
    );
  const selectedBeneficiaryAccount = beneficiaryAccountOptions?.find(
    (opt) => opt.value === selectedBeneficiaryAccountId,
  )?.beneficiaryAccount;

  useEffect(() => {
    if (businessEntityOptions?.length === 1 && !selectedBusinessEntityId) {
      form.setFieldValue("businessEntityId", businessEntityOptions[0].value);
    }
  }, [
    businessEntityOptions?.length,
    businessEntityOptions?.[0]?.value,
    selectedBusinessEntityId,
    form,
  ]);

  useEffect(() => {
    if (
      beneficiaryAccountOptions?.length === 1 &&
      !selectedBeneficiaryAccountId
    ) {
      form.setFieldValue(
        "beneficiaryAccountId",
        beneficiaryAccountOptions[0].value,
      );
    }
  }, [
    beneficiaryAccountOptions?.length,
    beneficiaryAccountOptions?.[0]?.value,
    selectedBeneficiaryAccountId,
    form,
  ]);

  return (
    <AdminPageContainer testId="admin-payouts-new2">
      <AdminPageHeader title="New Payout" subtitle="Create a new payout" />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className="flex max-w-lg flex-col gap-8">
          <form.AppField
            name="organisationId"
            listeners={{
              onChange: () => {
                form.resetField("businessEntityId");
                form.resetField("beneficiaryAccountId");
              },
            }}
          >
            {(field) => (
              <field.SelectField
                label={
                  <Txt size="sm" weight="semibold">
                    Organisation
                  </Txt>
                }
                placeholder="Select..."
              >
                {organisationOptions.map((o) => (
                  <field.SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </field.SelectItem>
                ))}
              </field.SelectField>
            )}
          </form.AppField>
          <form.AppField
            name="businessEntityId"
            listeners={{
              onChange: () => {
                form.resetField("beneficiaryAccountId");
              },
            }}
          >
            {(field) => (
              <field.SelectField
                label={
                  <Txt size="sm" weight="semibold">
                    Business entity
                  </Txt>
                }
                disabled={!selectedOrganisationId}
                placeholder={
                  !selectedOrganisationId
                    ? "Select organisation first"
                    : "Select..."
                }
              >
                {businessEntityOptions?.map((be) => (
                  <field.SelectItem key={be.value} value={be.value}>
                    {be.label}
                  </field.SelectItem>
                ))}
              </field.SelectField>
            )}
          </form.AppField>
          <form.AppField name="beneficiaryAccountId">
            {(field) => (
              <field.SelectField
                label={
                  <Txt size="sm" weight="semibold">
                    Beneficiary account
                  </Txt>
                }
                disabled={!selectedBusinessEntityId}
                placeholder={
                  !selectedBusinessEntityId
                    ? "Select source business entity first"
                    : "Select..."
                }
              >
                {beneficiaryAccountOptions?.map((a) => (
                  <field.SelectItem key={a.value} value={a.value}>
                    <AssetCard
                      beneficiaryAccount={a.beneficiaryAccount}
                      variant="IDENTIFIER"
                    />
                  </field.SelectItem>
                ))}
              </field.SelectField>
            )}
          </form.AppField>
          <div>
            <form.AppField name="amount">
              {(field) => (
                <field.CurrencyAmountField
                  currency={selectedBeneficiaryAccount?.currency ?? "AUD"}
                  align="start"
                  label={
                    <Txt size="sm" weight="semibold">
                      Amount
                    </Txt>
                  }
                  placeholder={"Amount"}
                />
              )}
            </form.AppField>
          </div>
          <form.AppField name="schedule">
            {(field) => (
              <ConnectedAnnotatedField
                label={
                  <Txt size="sm" weight="semibold">
                    Date
                  </Txt>
                }
              >
                <Tabs
                  value={field.state.value}
                  onValueChange={(o) =>
                    field.handleChange(o as (typeof scheduleValues)[number])
                  }
                >
                  <TabsList size="sm">
                    <TabsTrigger className="w-full" value="NOW">
                      Now
                    </TabsTrigger>
                    <TabsTrigger className="w-full" value="LATER">
                      Later
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </ConnectedAnnotatedField>
            )}
          </form.AppField>
          {selectedSchedule === "LATER" && (
            <form.AppField name="date">
              {(field) => (
                <div className="flex justify-start">
                  <field.DatePickerField placeholder={"Pick a date"} />
                </div>
              )}
            </form.AppField>
          )}
          <form.AppField name="reference">
            {(field) => (
              <field.TextareaField
                label={
                  <Txt size="sm" weight="semibold">
                    Reference
                  </Txt>
                }
              />
            )}
          </form.AppField>

          <form.AppField name="suppressNotifications">
            {(field) => (
              <field.SwitchField
                label={
                  <Txt size="sm" weight="semibold">
                    Do not send notifications
                  </Txt>
                }
              />
            )}
          </form.AppField>

          <div className="flex justify-end">
            <Button type="submit" disabled={loading}>
              Create payout
            </Button>
          </div>
        </div>
      </form>
    </AdminPageContainer>
  );
}
